/**
 * career-writing-style — W4 »Schreibstil«, route `/workshops/writing`.
 *
 * The fourth workshop is a bespoke THREE-ROUND track on the workshop stream (the same
 * shape the W1 Werte-track has), and this surface is its runner. It is deliberately a
 * SIBLING of `career-values-interview.ts`, not a new invention: the round stack, the
 * honest »thinking« state, the auto-generating frontier round and the per-block
 * selection state are that surface's mechanics, and the answer CONTROLS come from the
 * shared `app/lib/elicitation-card.ts` — never re-derived here.
 *
 * The three rounds (operator's flow, binding):
 *   ① `prose`      — the person turns a provided bullet set into free text. This is the
 *                     ONE place free text is explicitly wanted, so it uses the shared
 *                     open-text seam (`openTextField` / `readOpenText`).
 *   ② `scenarios`  — ~3 invented situations, each with TWO style variants of the SAME
 *                     content. Per situation: pick the closer variant → rate the fit →
 *                     optionally rewrite it in their own words. Rendered as ONE question
 *                     SET with auto-advance (all three on screen, the frontier
 *                     highlighted and scrolled to), confirmed as ONE batch. The pick is
 *                     the shared `pairwise` reaction grid, so the control a person learns
 *                     in W1 is the control they meet here.
 *   ③ `validation` — a text written IN the derived style. Confirm or edit; THE EDIT WINS
 *                     (`career-werte-sektion.ts` is the HITL precedent), and the confirm
 *                     is what emits the versioned `WritingStyleProfile` asset.
 *
 * Presentation: §0 seamless document — one reading column, a marker/connector spine, no
 * boxed cards. Containment appears only where there is a genuine active input. The
 * finished asset is rendered by the SHARED face `app/lib/writing-style-block.ts`, so the
 * workshop and the Kompass can never drift into two looks for one artifact.
 *
 * PUSH-FIRST (the W1 sibling's treatment): the surface claims the writing-style
 * workshop stream and re-reads the track when it moves, so a round produced by a call
 * whose response never came back — or by the person's other client — lands here on its
 * own. The re-read never GENERATES (that stays on the mount/answer paths, or a pushed
 * event would spend credits by itself) and stands down while a call is in flight.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
  type Urn,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { seamAutoGrow } from "@platform/rlhf-elements"
import {
  emptySelection,
  isCardDegraded,
  openTextField,
  readOpenText,
  renderCardInputs,
  type AnswerCard,
  type AnswerSelection,
} from "../lib/elicitation-card"
import { actionBody, actionClass } from "../lib/action"
import { writingStyleBlock } from "../lib/writing-style-block"
import type {
  WritingBulletSet,
  WritingScenario,
  WritingScenarioVerdict,
  WritingStyleProfile,
  WritingStyleRound,
} from "@career/definition-v2"
import { I18nToken, PushToken, RouterToken, SessionToken, WorkshopStoreToken, apiGet, apiPost } from "../services"
import { careerStream, workshopUrn, WORKSHOP_AGGREGATE, type ShellSession, type WorkshopStore } from "../domain"
import { acquireStreams, onChange } from "../lib/push-streams"
import { createWritingStyleDraftStore, isEmptyWritingStyleDraft, type ScenarioAnswerDraft, type WritingStyleDraftStore } from "../lib/writing-style-draft"
import type { CareerI18n, CareerKey } from "../../i18n"
import { nextStep } from "../lib/next-step"
import { forwardDecl, toNextStep } from "../golden-path"

// ─── The track view the api returns (contract `WritingTrackView`) ───────────────
// Read TOLERANTLY: every field is optional on the wire and normalised once, so a
// server that has not yet produced a round (or an older event shape) degrades to an
// empty default rather than throwing halfway down a template.

/** Round ③'s stimulus — the text written in the derived style, and what it was for. */
interface WritingValidationView {
  text: string
  /** Null on the wire when the round-③ text was written without a stated occasion. */
  context?: string | null
}

/** The wire shape — every field optional (tolerant fold, tolerant read). */
interface WritingTrackWire {
  bullets?: WritingBulletSet | null
  prose?: string | null
  scenarios?: WritingScenario[] | null
  verdicts?: WritingScenarioVerdict[] | null
  validation?: WritingValidationView | null
  profile?: WritingStyleProfile | null
  round?: WritingStyleRound | null
  complete?: boolean | null
}

/** The normalised track state every read site below may trust. */
interface WritingTrackView {
  bullets: WritingBulletSet | null
  prose: string | null
  scenarios: WritingScenario[]
  verdicts: WritingScenarioVerdict[]
  validation: WritingValidationView | null
  profile: WritingStyleProfile | null
  round: WritingStyleRound
  complete: boolean
}

/** The workshop-list entry W4's honest gate is read from (`/api/workshops`). */
interface WorkshopGateView {
  type: string
  gate: "locked" | "available" | "in_progress" | "completed"
  blockedBy: { slug: string; name: string } | null
}

/** The verdict fit vocabulary, straight off the domain type — never re-spelled. */
type WritingFit = WritingScenarioVerdict["fit"]

/** Where a round sits on the stack — exactly one is `current` while work remains. */
export type RoundState = "done" | "current" | "upcoming"

/** The three rounds in their canonical order (also the reading order of the stack). */
export const WRITING_ROUNDS: readonly WritingStyleRound[] = ["prose", "scenarios", "validation"]

/** The three fit answers in their canonical order — best fit first. */
export const WRITING_FITS: readonly WritingFit[] = ["trifft", "fast", "daneben"]

const ROUND_TITLE: Record<WritingStyleRound, CareerKey> = {
  prose: "writing.stage.prose",
  scenarios: "writing.stage.scenarios",
  validation: "writing.stage.validation",
}

const ROUND_THINKING: Record<WritingStyleRound, CareerKey> = {
  prose: "writing.gen.bullets",
  scenarios: "writing.gen.scenarios",
  validation: "writing.gen.validation",
}

/** The AI verb that produces each round's stimulus (metered server-side). */
const GENERATE_PATH: Record<WritingStyleRound, string> = {
  prose: "/api/workshops/writing/bullets",
  scenarios: "/api/workshops/writing/scenarios",
  validation: "/api/workshops/writing/validate",
}

/** Round state → the app's REGISTERED AX state class (`VOCABULARY.stateClasses`). */
const NODE_STATE_CLASS: Record<RoundState, string> = { done: "is-done", current: "is-current", upcoming: "is-todo" }

const FIT_LABEL: Record<WritingFit, CareerKey> = {
  trifft: "writing.fit.trifft",
  fast: "writing.fit.fast",
  daneben: "writing.fit.daneben",
}

/** A round's stimulus is present — the surface may render its body instead of »denkt nach«. */
function hasStimulus(view: WritingTrackView, round: WritingStyleRound): boolean {
  if (round === "prose") return view.bullets !== null
  if (round === "scenarios") return view.scenarios.length > 0
  return view.validation !== null
}

/** A round is DONE when the person's own answer for it exists (never when the AI answered). */
function roundDone(view: WritingTrackView, round: WritingStyleRound): boolean {
  if (round === "prose") return view.prose !== null
  if (round === "scenarios") return view.verdicts.length > 0
  return view.profile !== null
}

/** The first round the person still owes an answer for (null = the track is finished). */
function deriveRound(view: Omit<WritingTrackView, "round">): WritingStyleRound {
  if (view.prose === null) return "prose"
  if (view.verdicts.length === 0) return "scenarios"
  return "validation"
}

/** Normalise the wire into the shape the templates read. Total — never throws. */
function normalizeTrack(wire: WritingTrackWire): WritingTrackView {
  const prose = typeof wire.prose === "string" && wire.prose.trim().length > 0 ? wire.prose : null
  const partial = {
    bullets: wire.bullets ?? null,
    prose,
    scenarios: wire.scenarios ?? [],
    verdicts: wire.verdicts ?? [],
    validation: wire.validation ?? null,
    profile: wire.profile ?? null,
    complete: wire.complete ?? false,
  }
  return { ...partial, round: wire.round ?? deriveRound(partial) }
}

/**
 * One scenario as the SHARED `pairwise` answer card — two poles, one »näher an mir«
 * column. Reusing the elicitation grid (rather than drawing two boxes here) is the
 * standing rule: the control a person learned in W1 is the control they meet in W4.
 * The grid stamps its picks positionally (`A`/`B`), so {@link variantIdOf} maps back.
 */
function scenarioCard(scenario: WritingScenario): AnswerCard {
  const [a, b] = scenario.variants
  return {
    primitive: "pairwise",
    prompt: scenario.situation,
    ...(a ? { optionA: { id: a.id, label: a.label, detail: a.text } } : {}),
    ...(b ? { optionB: { id: b.id, label: b.label, detail: b.text } } : {}),
  }
}

/** Resolve the grid's positional pick (`A`/`B`) back to the variant's real id. */
function variantIdOf(scenario: WritingScenario, pole: string | null): string | null {
  if (pole === null) return null
  const variant = pole === "A" ? scenario.variants[0] : pole === "B" ? scenario.variants[1] : undefined
  return variant?.id ?? null
}

/** The pole (`A`/`B`) a stored verdict corresponds to — so a re-opened round shows the pick. */
function poleOf(scenario: WritingScenario, variantId: string): string | null {
  const index = scenario.variants.findIndex((v) => v.id === variantId)
  return index === 0 ? "A" : index === 1 ? "B" : null
}

export class WritingStyleVm implements ViewModel {
  readonly view: Signal<WritingTrackView | null> = signal<WritingTrackView | null>(null)
  /** W4's honest gate (`PREREQ.writing_style = "self_knowledge"`), read off `/api/workshops`. */
  readonly gate: Signal<WorkshopGateView | null> = signal<WorkshopGateView | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  /** An ANSWER (prose / verdicts / confirm) is in flight. */
  readonly busy: Signal<boolean> = signal(false)
  /** A round's STIMULUS is being generated — the honest »thinking« state between rounds. */
  readonly gen: Signal<boolean> = signal(false)
  /** Round ②'s in-progress picks + fit ratings, keyed by scenario id. */
  readonly picks: Signal<Record<string, AnswerSelection>> = signal<Record<string, AnswerSelection>>({})
  readonly fits: Signal<Record<string, WritingFit>> = signal<Record<string, WritingFit>>({})
  /** Round ①'s free text. Mirrored out of the field because the DOM alone cannot survive the
   *  end of a page's life — and this text is written from scratch, so it exists nowhere else. */
  readonly prose: Signal<string> = signal("")
  /** Round ②'s per-scenario rewrites, keyed by scenario id — same reason as {@link prose}. */
  readonly rewrites: Signal<Record<string, string>> = signal<Record<string, string>>({})
  /** Round ③'s in-progress rewrite of the proposal; null while the editor is closed. */
  readonly editText: Signal<string | null> = signal<string | null>(null)
  /** An already-answered round the person re-opened (»ändern«) — it becomes the expanded one. */
  readonly reopened: Signal<WritingStyleRound | null> = signal<WritingStyleRound | null>(null)
  /** Round ③: the person chose to rewrite the proposed text rather than accept it. */
  readonly editing: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  /** Per-user backup of every unsubmitted answer — see `writing-style-draft`. */
  private readonly draft: WritingStyleDraftStore
  /**
   * The situations the person has answered in THIS page-life. `seedPicks` fills `picks`/`fits`
   * from verdicts already on the stream, and those are durable — backing them up would make
   * the draft a mirror of the server rather than a rescue for unsaved work, and it would keep
   * a slot alive forever. Only what was touched here is at risk, so only that is persisted.
   */
  private readonly touchedScenarios = new Set<string>()
  /** True once the person has TYPED in round ③'s editor — merely opening it is not work. */
  private editTouched = false

  constructor(
    private readonly router: Router,
    i18n: CareerI18n,
    private readonly session: ShellSession,
    private readonly push: PushChannel,
    private readonly store: WorkshopStore,
  ) {
    this.t = i18n.t
    this.draft = createWritingStyleDraftStore(session.user?.sub ?? null)
    this.renderDeps = [
      this.view,
      this.gate,
      this.loaded,
      this.error,
      this.busy,
      this.gen,
      this.picks,
      this.fits,
      this.prose,
      this.rewrites,
      this.editText,
      this.reopened,
      this.editing,
    ]
  }

  attach(): () => void {
    void this.refresh().then(() => this.maybeGenerate())
    const userId = this.session.user?.sub
    if (!userId) return () => {}
    const release = acquireStreams(this.push, [careerStream(userId, WORKSHOP_AGGREGATE, workshopUrn(userId, "writing_style"))])
    const stop = onChange(this.store.entity(workshopUrn(userId, "writing_style")), () => this.pushRefresh())
    return () => {
      stop()
      release()
    }
  }

  /** The push-driven re-read: server state only, never a generation, and never on top
   *  of a call that is still in flight. Unsubmitted answers survive it — `refresh()`
   *  restores the local draft AFTER the server seed, which is exactly that guarantee. */
  private pushRefresh(): void {
    if (this.busy.value || this.gen.value) return
    void this.refresh()
  }

  private async refresh(): Promise<void> {
    try {
      // The gate read is defensive: a missing/failed workshop list degrades to »no gate
      // known« (the track itself still answers), never to a blank page.
      const [wire, list] = await Promise.all([
        apiGet<WritingTrackWire>("/api/workshops/writing"),
        apiGet<WorkshopGateView[]>("/api/workshops").catch(() => null),
      ])
      const view = normalizeTrack(wire)
      this.view.set(view)
      this.gate.set(list?.find((w) => w.type === "writing_style") ?? null)
      this.seedPicks(view)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      // AFTER the server seed, so the unsubmitted answers win over the durable ones: a draft
      // only ever exists for work the server has not accepted yet. Restoring outside the try
      // means an offline / just-expired session still gets its answers back.
      this.restoreDraft()
      this.loaded.set(true)
    }
  }

  /**
   * Mirror every unsubmitted answer into the local draft. Called on EVERY mutation — a pick, a
   * fit rating, a keystroke — because until the round's POST lands, this is the only copy that
   * survives a reload or the 401 route-to-login round-trip.
   */
  private saveDraft(): void {
    const scenarios: Record<string, ScenarioAnswerDraft> = {}
    for (const id of this.touchedScenarios) {
      scenarios[id] = {
        choiceId: this.picks.value[id]?.choiceId ?? null,
        fit: this.fits.value[id] ?? null,
        rewrite: this.rewrites.value[id] ?? "",
      }
    }
    const snapshot = {
      prose: this.prose.value,
      scenarios,
      edit: this.editTouched ? this.editText.value : null,
      updatedAt: new Date().toISOString(),
    }
    // An empty snapshot is not a draft — never leave a slot behind that restores nothing.
    if (isEmptyWritingStyleDraft(snapshot)) this.draft.clear()
    else this.draft.save(snapshot)
  }

  /** Put a rescued draft back into the controls it came from. */
  private restoreDraft(): void {
    const draft = this.draft.load()
    if (!draft) return
    this.prose.set(draft.prose)
    this.editText.set(draft.edit)
    // An open round-③ editor is part of the work: restore the editor, not just its text.
    if (draft.edit !== null) {
      this.editing.set(true)
      this.editTouched = true
    }
    const picks = { ...this.picks.value }
    const fits = { ...this.fits.value }
    const rewrites: Record<string, string> = {}
    for (const [id, answer] of Object.entries(draft.scenarios)) {
      // Rescued work is still unsubmitted work — keep backing it up from here on.
      this.touchedScenarios.add(id)
      picks[id] = { ...(picks[id] ?? emptySelection()), choiceId: answer.choiceId }
      if (answer.fit !== null) fits[id] = answer.fit
      else delete fits[id]
      if (answer.rewrite.length > 0) rewrites[id] = answer.rewrite
    }
    this.picks.set(picks)
    this.fits.set(fits)
    this.rewrites.set(rewrites)
  }

  /** Round ①'s field text, draft-backed. */
  setProse(text: string): void {
    this.prose.set(text)
    this.saveDraft()
  }

  /** One situation's rewrite, draft-backed. */
  setRewrite(scenarioId: string, text: string): void {
    this.rewrites.set({ ...this.rewrites.value, [scenarioId]: text })
    this.touchedScenarios.add(scenarioId)
    this.saveDraft()
  }

  /** Round ③'s editor text, draft-backed. */
  setEditText(text: string): void {
    this.editText.set(text)
    this.editTouched = true
    this.saveDraft()
  }

  /** Seed round ②'s controls from the verdicts already on the stream, so re-opening the
   *  round shows what the person actually answered instead of an empty grid. */
  private seedPicks(view: WritingTrackView): void {
    if (view.verdicts.length === 0) return
    const picks: Record<string, AnswerSelection> = {}
    const fits: Record<string, WritingFit> = {}
    for (const verdict of view.verdicts) {
      const scenario = view.scenarios.find((s) => s.id === verdict.scenarioId)
      if (!scenario) continue
      picks[scenario.id] = { ...emptySelection(), choiceId: poleOf(scenario, verdict.chosenVariant) }
      fits[scenario.id] = verdict.fit
    }
    this.picks.set(picks)
    this.fits.set(fits)
  }

  /** The active round — the first one the person still owes an answer for. */
  activeRound(): WritingStyleRound {
    const view = this.view.value
    return view ? view.round : "prose"
  }

  /** The ONE expanded round — a re-opened answered round wins over the frontier. */
  expandedRound(): WritingStyleRound | null {
    const view = this.view.value
    if (!view) return null
    const reopened = this.reopened.value
    if (reopened !== null) return reopened
    return view.complete || view.profile !== null ? null : this.activeRound()
  }

  roundState(round: WritingStyleRound, expanded: WritingStyleRound | null): RoundState {
    const view = this.view.value
    if (round === expanded) return "current"
    if (view && roundDone(view, round)) return "done"
    return "upcoming"
  }

  /** Re-open an answered round for a corrected answer (»ändern«). */
  reopen(round: WritingStyleRound): void {
    this.reopened.set(round)
    this.editing.set(false)
    this.maybeGenerate()
  }

  selFor(scenarioId: string): AnswerSelection {
    return this.picks.value[scenarioId] ?? emptySelection()
  }

  pickVariant(scenarioId: string, pole: string): void {
    const current = this.selFor(scenarioId)
    this.picks.set({ ...this.picks.value, [scenarioId]: { ...current, choiceId: current.choiceId === pole ? null : pole } })
    this.touchedScenarios.add(scenarioId)
    this.saveDraft()
  }

  setFit(scenarioId: string, fit: WritingFit): void {
    const next = { ...this.fits.value }
    if (next[scenarioId] === fit) delete next[scenarioId]
    else next[scenarioId] = fit
    this.fits.set(next)
    this.touchedScenarios.add(scenarioId)
    this.saveDraft()
  }

  /** Auto-advance: the frontier situation is the first one still without a fit rating. */
  activeScenarioId(): string | null {
    const scenarios = this.view.value?.scenarios ?? []
    return scenarios.find((s) => this.fits.value[s.id] === undefined)?.id ?? null
  }

  /** How many of the set are settled — the honest progress line above the batch confirm. */
  settledCount(): number {
    const scenarios = this.view.value?.scenarios ?? []
    return scenarios.filter((s) => this.fits.value[s.id] !== undefined && this.selFor(s.id).choiceId !== null).length
  }

  /** The whole SET is answered — only then may the batch be confirmed (one confirm, one batch). */
  scenariosReady(): boolean {
    const scenarios = this.view.value?.scenarios ?? []
    return scenarios.length > 0 && this.settledCount() === scenarios.length
  }

  /** Generate ONE round's stimulus (the metered AI verb). */
  async generate(round: WritingStyleRound): Promise<void> {
    if (this.gen.value || this.busy.value) return
    this.gen.set(true)
    this.error.set(null)
    try {
      this.view.set(normalizeTrack(await apiPost<WritingTrackWire>(GENERATE_PATH[round])))
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.gen.set(false)
    }
  }

  /** A freshly-reached round with no stimulus yet generates it — the interview's auto-advance. */
  maybeGenerate(): void {
    const view = this.view.value
    const round = this.expandedRound()
    if (!view || round === null || this.gate.value?.gate === "locked") return
    if (!hasStimulus(view, round) && !this.gen.value && !this.busy.value) void this.generate(round)
  }

  private async answer(path: string, body?: Record<string, unknown>): Promise<void> {
    if (this.busy.value) return
    this.busy.set(true)
    this.error.set(null)
    try {
      const view = normalizeTrack(await apiPost<WritingTrackWire>(path, body))
      this.view.set(view)
      this.reopened.set(null)
      this.editing.set(false)
      this.seedPicks(view)
      // The round is durable server-side now, and the next one is unanswered — so EVERY
      // unsubmitted answer this draft was holding has done its job.
      this.prose.set("")
      this.rewrites.set({})
      this.editText.set(null)
      this.touchedScenarios.clear()
      this.editTouched = false
      this.draft.clear()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(false)
    }
    this.maybeGenerate() // the answered round advances the frontier → generate the next set
  }

  /** Round ① — the sanctioned free text. */
  async submitProse(text: string): Promise<void> {
    if (text.trim().length === 0) return
    await this.answer("/api/workshops/writing/prose", { text: text.trim() })
  }

  /** Round ② — ALL verdicts as ONE batch (question SETS per round, never page by page). */
  async submitVerdicts(verdicts: WritingScenarioVerdict[]): Promise<void> {
    if (verdicts.length === 0) return
    await this.answer("/api/workshops/writing/verdicts", { verdicts })
  }

  /** Round ③ — the HITL confirm that emits the asset. An edit REPLACES the proposal. */
  async confirm(edit?: string): Promise<void> {
    const trimmed = edit?.trim() ?? ""
    await this.answer("/api/workshops/writing/confirm", trimmed.length > 0 ? { edit: trimmed } : {})
  }

  /**
   * Open round ③'s editor on the proposal. Not itself draft-worthy — the box still holds the
   * server's own text, which a reload re-reads anyway; the first keystroke starts the backup.
   */
  startEdit(proposal: string): void {
    this.editing.set(true)
    this.editText.set(proposal)
  }
  cancelEdit(): void {
    this.editing.set(false)
    this.editText.set(null)
    this.editTouched = false
    this.saveDraft()
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerWritingStyle extends KitElement<WritingStyleVm> {
  protected createViewModel(ctx: KitContext): WritingStyleVm {
    return new WritingStyleVm(
      ctx.inject(RouterToken),
      ctx.inject(I18nToken),
      ctx.inject(SessionToken),
      ctx.inject(PushToken),
      ctx.inject(WorkshopStoreToken),
    )
  }

  // ── The round SPINE: three nodes, exactly one expanded ────────────────────────

  /** The honest »thinking« state while a round's stimulus is assembled. */
  private thinking(vm: WritingStyleVm, round: WritingStyleRound): TemplateResult {
    return html`<div class="wst-thinking" role="status" aria-live="polite">
      <span class="wst-thinking-dots" aria-hidden="true"><span></span><span></span><span></span></span>
      <span lang="de">${vm.t(ROUND_THINKING[round])}</span>
    </div>`
  }

  /** A stimulus that never arrived (the generate call failed) — a retry, not a dead end.
   *  The retry regenerates the EXPANDED round, which is by definition the one missing it. */
  private failed(vm: WritingStyleVm): TemplateResult {
    return html`<div class="wst-failed" role="status">
      <p class="surface-empty" lang="de">${vm.t("writing.gen.failed")}</p>
      <button
        class=${classMap({ ...clsMap(actionClass({ variant: "secondary" })), "is-pending": vm.gen.value })}
        data-act="generate-stimulus" ?disabled=${vm.gen.value} lang="de"
      >${actionBody({ act: "generate-stimulus", label: vm.t("writing.gen.retry"), variant: "secondary", intent: "refresh" })}</button>
    </div>`
  }

  // ── Round ① »prose« — the bullet set + the sanctioned free-text seam ──────────

  private proseBody(vm: WritingStyleVm, view: WritingTrackView): TemplateResult {
    const bullets = view.bullets
    if (!bullets) return vm.gen.value ? this.thinking(vm, "prose") : this.failed(vm)
    return html`<div class="wst-prose">
      <p class="wst-brief" lang="de">${bullets.brief}</p>
      <ul class="wst-bullets" role="list">
        ${bullets.bullets.map((line) => html`<li class="wst-bullet" lang="de">${line}</li>`)}
      </ul>
      ${bullets.grounding
        ? html`<p class="wst-grounding" lang="de">
            <span class="wst-grounding-label" lang="de">${vm.t("writing.prose.groundingLabel")}</span> ${bullets.grounding}
          </p>`
        : nothing}
      <p class="wst-hint" lang="de">${vm.t("writing.prose.hint")}</p>
      ${openTextField(vm.t("writing.prose.placeholder"), vm.t("writing.prose.aria"), {
        value: vm.prose.value,
        onInput: (text) => vm.setProse(text),
      })}
      <div class="wst-foot">
        <button
          class=${classMap({ ...clsMap(actionClass({ variant: "primary" })), "is-pending": vm.busy.value })}
          data-act="submit-turn" data-value="prose" ?disabled=${vm.busy.value} lang="de"
        >${actionBody({
          act: "submit-turn",
          label: vm.t("writing.prose.submit"),
          variant: "primary",
          pending: vm.busy.value,
          pendingLabel: vm.t("writing.prose.submitting"),
        })}</button>
      </div>
    </div>`
  }

  // ── Round ② »scenarios« — the question SET with auto-advance ──────────────────

  /** ONE situation: the framing, the two variants (SHARED pairwise grid), the fit, the rewrite. */
  private scenario(vm: WritingStyleVm, scenario: WritingScenario, active: boolean): TemplateResult {
    const card = scenarioCard(scenario)
    const sel = vm.selFor(scenario.id)
    const fit = vm.fits.value[scenario.id]
    const picked = sel.choiceId !== null
    return html`<li
      class=${classMap({ "wst-scn": true, "is-active": active, "is-selected": fit !== undefined, "has-accent": active })}
      data-scenario=${scenario.id}
    >
      <p class="wst-scn-situation" lang="de">${scenario.situation}</p>
      ${scenario.grounding
        ? html`<details class="wst-scn-why">
            <summary lang="de">${vm.t("writing.scenarios.groundingLabel")}</summary>
            <p lang="de">${scenario.grounding}</p>
          </details>`
        : nothing}
      ${isCardDegraded(card)
        ? html`<p class="surface-empty" lang="de">${vm.t("writing.scenarios.degraded")}</p>`
        : renderCardInputs(card, {
            id: scenario.id,
            sel,
            submitting: vm.busy.value,
            labels: {
              choose: vm.t("writing.scenarios.pick"),
              best: vm.t("writing.scenarios.pick"),
              worst: vm.t("writing.scenarios.pick"),
              veto: vm.t("writing.scenarios.pick"),
              openPlaceholder: vm.t("writing.scenarios.rewritePlaceholder"),
              whyLabel: vm.t("writing.scenarios.rewriteLabel"),
              whyPlaceholder: vm.t("writing.scenarios.rewritePlaceholder"),
            },
          })}
      ${picked ? this.fitRow(vm, scenario.id, fit) : nothing}
      ${picked ? this.rewriteField(vm, scenario.id) : nothing}
    </li>`
  }

  /** The fit rating — three quiet pills composing the §6 action chrome (a toggle needs
   *  `aria-pressed`, which `action()` does not emit, so the element is written here). */
  private fitRow(vm: WritingStyleVm, scenarioId: string, chosen: WritingFit | undefined): TemplateResult {
    return html`<div class="wst-fit" role="group" aria-label=${vm.t("writing.scenarios.fitLabel")}>
      <span class="wst-fit-label" lang="de">${vm.t("writing.scenarios.fitLabel")}</span>
      ${WRITING_FITS.map(
        (fit) => html`<button
          class=${classMap({ ...clsMap(actionClass({ variant: "quiet", className: "wst-fit-opt" })), "is-chosen": chosen === fit })}
          data-act="pick-option" data-role="fit" data-block=${scenarioId} data-id=${fit}
          aria-pressed=${chosen === fit ? "true" : "false"} ?disabled=${vm.busy.value} lang="de"
        >${actionBody({ act: "pick-option", label: vm.t(FIT_LABEL[fit]), variant: "quiet" })}</button>`,
      )}
    </div>`
  }

  /** The REWRITE — the highest-signal artifact in the whole workshop, so it gets real room:
   *  its own label, its own reason for existing, and the shared open-text seam beneath. */
  private rewriteField(vm: WritingStyleVm, scenarioId: string): TemplateResult {
    return html`<div class="wst-rewrite">
      <p class="wst-rewrite-label" lang="de">${vm.t("writing.scenarios.rewriteLabel")}</p>
      <p class="wst-rewrite-hint" lang="de">${vm.t("writing.scenarios.rewriteHint")}</p>
      ${openTextField(vm.t("writing.scenarios.rewritePlaceholder"), vm.t("writing.scenarios.rewriteLabel"), {
        value: vm.rewrites.value[scenarioId] ?? "",
        onInput: (text) => vm.setRewrite(scenarioId, text),
      })}
    </div>`
  }

  private scenariosBody(vm: WritingStyleVm, view: WritingTrackView): TemplateResult {
    if (view.scenarios.length === 0) return vm.gen.value ? this.thinking(vm, "scenarios") : this.failed(vm)
    const activeId = vm.activeScenarioId()
    const ready = vm.scenariosReady()
    return html`<div class="wst-scenarios">
      <p class="wst-lede" lang="de">${vm.t("writing.scenarios.lede")}</p>
      <ol class="wst-scn-list" role="list">
        ${view.scenarios.map((scenario) => this.scenario(vm, scenario, scenario.id === activeId))}
      </ol>
      <div class="wst-foot">
        <button
          class=${classMap({ ...clsMap(actionClass({ variant: "primary" })), "is-pending": vm.busy.value })}
          data-act="submit-turn" data-value="scenarios" ?disabled=${vm.busy.value || !ready} lang="de"
        >${actionBody({
          act: "submit-turn",
          label: vm.t("writing.scenarios.submit"),
          variant: "primary",
          pending: vm.busy.value,
          pendingLabel: vm.t("writing.scenarios.submitting"),
        })}</button>
        <p class="wst-progress" role="status" lang="de">
          ${vm.t("writing.scenarios.progress", { done: String(vm.settledCount()), total: String(view.scenarios.length) })}
        </p>
      </div>
    </div>`
  }

  // ── Round ③ »validation« — the HITL confirm; the person's edit WINS ────────────

  private validationBody(vm: WritingStyleVm, view: WritingTrackView): TemplateResult {
    const validation = view.validation
    if (!validation) return vm.gen.value ? this.thinking(vm, "validation") : this.failed(vm)
    if (vm.editing.value) return this.validationEditor(vm, validation.text)
    return html`<div class="wst-validation">
      <p class="wst-lede" lang="de">${vm.t("writing.validation.lede")}</p>
      ${validation.context
        ? html`<p class="wst-grounding" lang="de">
            <span class="wst-grounding-label" lang="de">${vm.t("writing.validation.contextLabel")}</span> ${validation.context}
          </p>`
        : nothing}
      <p class="wst-sample" lang="de">${validation.text}</p>
      <div class="wst-foot">
        <button
          class=${classMap({ ...clsMap(actionClass({ variant: "primary" })), "is-pending": vm.busy.value })}
          data-act="confirm" ?disabled=${vm.busy.value} lang="de"
        >${actionBody({
          act: "confirm",
          label: vm.t("writing.validation.confirm"),
          variant: "primary",
          intent: "confirm",
          pending: vm.busy.value,
          pendingLabel: vm.t("writing.validation.confirming"),
        })}</button>
        <button class=${actionClass({ variant: "quiet" })} data-act="edit-start" data-value="validation" ?disabled=${vm.busy.value} lang="de">
          ${actionBody({ act: "edit-start", label: vm.t("writing.validation.edit"), variant: "quiet", intent: "edit" })}
        </button>
      </div>
    </div>`
  }

  /** The edit surface — the proposal is loaded INTO the field, so »anpassen« means changing
   *  a real text, and what the person leaves behind is what the asset records. */
  private validationEditor(vm: WritingStyleVm, text: string): TemplateResult {
    return html`<div class="wst-validation">
      <p class="wst-hint" lang="de">${vm.t("writing.validation.editHint")}</p>
      <textarea
        class="rlhf-seam wst-edit-input"
        rows="6"
        ${seamAutoGrow()}
        .value=${vm.editText.value ?? text}
        @input=${(e: Event) => vm.setEditText((e.target as HTMLTextAreaElement).value)}
        aria-label=${vm.t("writing.validation.edit")}
        lang="de"
      ></textarea>
      <div class="wst-foot">
        <button
          class=${classMap({ ...clsMap(actionClass({ variant: "primary" })), "is-pending": vm.busy.value })}
          data-act="edit-save" ?disabled=${vm.busy.value} lang="de"
        >${actionBody({
          act: "edit-save",
          label: vm.t("writing.validation.save"),
          variant: "primary",
          intent: "confirm",
          pending: vm.busy.value,
          pendingLabel: vm.t("writing.validation.confirming"),
        })}</button>
        <button class=${actionClass({ variant: "quiet" })} data-act="edit-cancel" lang="de">
          ${actionBody({ act: "edit-cancel", label: vm.t("writing.validation.cancel"), variant: "quiet", intent: "discard" })}
        </button>
      </div>
    </div>`
  }

  // ── Collapsed states ──────────────────────────────────────────────────────────

  /** An answered round — the title and what the PERSON produced, plus »ändern«. */
  private doneBody(vm: WritingStyleVm, view: WritingTrackView, round: WritingStyleRound): TemplateResult {
    const answer =
      round === "prose"
        ? (view.prose ?? "")
        : round === "scenarios"
          ? vm.t("writing.scenarios.answered", { n: String(view.verdicts.length) })
          : vm.t("writing.validation.answered")
    return html`<div class="wst-done-body">
      <p class="wst-node-title" lang="de">${vm.t(ROUND_TITLE[round])}</p>
      <p class=${classMap({ "wst-answer": true, "wst-quote": round === "prose" })} lang="de">${answer}</p>
      ${round === "validation"
        ? nothing
        : html`<button class="wst-change" data-act="reopen" data-value=${round} lang="de" aria-label=${vm.t("writing.node.changeAria", { round: vm.t(ROUND_TITLE[round]) })}>
            ${vm.t("writing.node.change")}
          </button>`}
    </div>`
  }

  /** A round still ahead — ONE quiet line (never its content: no spoiler, no prose). */
  private upcomingBody(vm: WritingStyleVm, round: WritingStyleRound): TemplateResult {
    return html`<p class="wst-node-title" lang="de">${vm.t(ROUND_TITLE[round])}</p>`
  }

  private currentBody(vm: WritingStyleVm, view: WritingTrackView, round: WritingStyleRound): TemplateResult {
    const body =
      round === "prose" ? this.proseBody(vm, view) : round === "scenarios" ? this.scenariosBody(vm, view) : this.validationBody(vm, view)
    return html`<div class="wst-round">
      <p class="wst-node-title is-current" lang="de">${vm.t(ROUND_TITLE[round])}</p>
      ${body}
    </div>`
  }

  /** ONE node on the spine: the state marker + the state-appropriate body. The class is
   *  the REGISTERED state vocabulary (`is-done` / `is-current` / `is-todo`), not a
   *  surface-local spelling of the same three ideas. */
  private node(state: RoundState, body: TemplateResult): TemplateResult {
    return html`<li class=${classMap({ "wst-node": true, [NODE_STATE_CLASS[state]]: true })}>
      <span class="wst-node-marker" aria-hidden="true"></span>
      <div class="wst-node-body">${body}</div>
    </li>`
  }

  private stack(vm: WritingStyleVm, view: WritingTrackView): TemplateResult {
    const expanded = vm.expandedRound()
    return html`<ol class="wst-stack" role="list">
      ${WRITING_ROUNDS.map((round) => {
        const state = vm.roundState(round, expanded)
        if (state === "current") return this.node(state, this.currentBody(vm, view, round))
        if (state === "done") return this.node(state, this.doneBody(vm, view, round))
        return this.node(state, this.upcomingBody(vm, round))
      })}
    </ol>`
  }

  // ── States ────────────────────────────────────────────────────────────────────

  /** Gated ≠ broken (AX-10): W4 invents its situations FROM the person's own material, so
   *  without W1 it would invent from nothing. Say that, and offer the door. */
  private locked(vm: WritingStyleVm): TemplateResult {
    const blocked = vm.gate.value?.blockedBy
    return html`<div class="wst-intro">
      <p class="surface-lead" lang="de">${vm.t("writing.locked.lede")}</p>
      ${blocked ? html`<p class="wst-hint" lang="de">${vm.t("writing.locked.blockedBy", { name: blocked.name })}</p>` : nothing}
      <button class=${actionClass({ variant: "primary" })} data-act="go" data-url="/workshops/values/sektion" lang="de">
        ${actionBody({ act: "go", label: vm.t("writing.locked.cta"), variant: "primary", intent: "forward" })}
      </button>
    </div>`
  }

  /** The finished track: the asset itself, rendered by the SHARED Kompass face. */
  private finished(vm: WritingStyleVm, profile: WritingStyleProfile): TemplateResult {
    return html`<div class="wst-finished">
      <h2 class="surface-section-title" lang="de">${vm.t("writing.done.title")}</h2>
      <p class="surface-lead" lang="de">${vm.t("writing.done.hint")}</p>
      ${writingStyleBlock(vm.t, profile)}
    </div>`
  }

  private body(vm: WritingStyleVm, view: WritingTrackView): TemplateResult {
    if (vm.gate.value?.gate === "locked") return this.locked(vm)
    return html`<div class="wst-layout">
      ${this.stack(vm, view)}
      ${view.profile ? this.finished(vm, view.profile) : nothing}
      ${view.profile ? this.goldenNext(vm) : nothing}
    </div>`
  }

  /** The golden-path forward affordance (D4) — W4 is a BRANCH off W1, and its product
   *  lands in the Kompass, so that is where it points. */
  private goldenNext(vm: WritingStyleVm): TemplateResult | typeof nothing {
    const decl = forwardDecl("career-writing-style")
    if (!decl) return nothing
    return nextStep(vm.t("next.eyebrow"), toNextStep(decl, vm.t(decl.labelKey)))
  }

  protected render(vm: WritingStyleVm): TemplateResult {
    const rootUrn = (this.urn ?? "urn:career:workshop:writing") as Urn
    const err = vm.error.value
    const view = vm.view.value
    return html`
      <section class="surface writing-style" urn=${rootUrn} aria-labelledby="wst-title">
        <p class="eyebrow" lang="de">${vm.t("writing.kicker")}</p>
        <h1 id="wst-title" class="surface-title" lang="de">${vm.t("surface.writingStyle.title")}</h1>
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}
        ${vm.loaded.value && view ? this.body(vm, view) : nothing}
      </section>`
  }

  protected override actions(vm: WritingStyleVm): ActionMap {
    return {
      // The AX verbs are the app's REGISTERED vocabulary (`VOCABULARY.actVerbs`,
      // app/domain.ts) wherever one already means what W4 means — the fit rating is a
      // `pick-option` in the `fit` role, both answers are a `submit-turn`, and the
      // round-③ rewrite is an `edit-save`. Only `reopen` is unregistered, and it is the
      // verb `career-values-interview` already ships (see the report's patch note).
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
      go: ({ url }) => {
        if (url) vm.go(url)
      },
      // The retry regenerates the EXPANDED round — the only one that can be missing one.
      "generate-stimulus": () => {
        const round = vm.expandedRound()
        if (round !== null) void vm.generate(round)
      },
      reopen: ({ value }) => {
        if (isRound(value)) vm.reopen(value)
      },
      // Round ②: the shared pairwise grid stamps `data-block` (the scenario id) on every
      // pick; the fit pills ride the same verb in their own role.
      "pick-option": ({ role, id, block }) => {
        if (!id || !block) return
        if (role === "fit") {
          if (!isFit(id)) return
          vm.setFit(block, id)
          this.advanceToNextScenario(vm)
          return
        }
        vm.pickVariant(block, id)
      },
      // Both of the person's own answers: round ①'s free text (read from the round's own
      // DOM scope) and round ②'s verdict batch.
      "submit-turn": ({ value }) => {
        if (value === "prose") void vm.submitProse(vm.prose.value)
        else if (value === "scenarios") void vm.submitVerdicts(this.readVerdicts(vm))
      },
      // Round ③: confirm the proposal as it stands, or save the person's own rewrite.
      confirm: () => void vm.confirm(),
      "edit-start": () => vm.startEdit(vm.view.value?.validation?.text ?? ""),
      "edit-cancel": () => vm.cancelEdit(),
      "edit-save": () => void vm.confirm(vm.editText.value ?? ""),
    }
  }

  /**
   * Auto-advance (the standing »question SETS per round, auto-advance« rule): rating a
   * situation's fit settles it and moves the reading position to the next unanswered one.
   * The whole set stays on screen — advancing is a scroll, never a page turn, so nothing
   * the person already typed can leave the DOM before the batch is confirmed.
   */
  private advanceToNextScenario(vm: WritingStyleVm): void {
    const nextId = vm.activeScenarioId()
    if (nextId === null) return
    queueMicrotask(() => {
      const target = this.querySelector<HTMLElement>(scenarioSelector(nextId))
      target?.scrollIntoView({ behavior: "smooth", block: "center" })
    })
  }

  /**
   * The whole round ② batch, built from the per-scenario selection state plus each scenario's
   * own rewrite.
   *
   * The rewrite is read from the VM, not from the `.wst-scn` scope in the DOM. Taking it off the
   * DOM made it hostage to whether the field happened to be MOUNTED: `pickVariant` toggles a
   * second click on the held pole back to null, which un-gates `rewriteField` and unmounted the
   * textarea — so de-selecting silently destroyed the rewrite, with no unload involved at all.
   * The signal survives that (and the person gets their text back when they re-pick).
   */
  private readVerdicts(vm: WritingStyleVm): WritingScenarioVerdict[] {
    const verdicts: WritingScenarioVerdict[] = []
    for (const scenario of vm.view.value?.scenarios ?? []) {
      const chosenVariant = variantIdOf(scenario, vm.selFor(scenario.id).choiceId)
      const fit = vm.fits.value[scenario.id]
      if (chosenVariant === null || fit === undefined) continue
      const rewrite = (vm.rewrites.value[scenario.id] ?? "").trim()
      verdicts.push({ scenarioId: scenario.id, chosenVariant, fit, ...(rewrite.length > 0 ? { rewrite } : {}) })
    }
    return verdicts
  }
}

/** A `data-value` narrowed to a real round — never trust a string off the DOM. */
function isRound(value: string | undefined): value is WritingStyleRound {
  return value !== undefined && (WRITING_ROUNDS as readonly string[]).includes(value)
}

/** A `data-value` narrowed to a real fit verdict. */
function isFit(value: string | undefined): value is WritingFit {
  return value !== undefined && (WRITING_FITS as readonly string[]).includes(value)
}

/** The attribute selector for ONE situation. `CSS.escape` is not present in every DOM
 *  implementation the tests run against, so the quoting is done here — scenario ids are
 *  model-authored strings and must never be pasted raw into a selector. */
function scenarioSelector(scenarioId: string): string {
  return `.wst-scn[data-scenario="${scenarioId.replace(/["\\]/g, "\\$&")}"]`
}

/** `classMap` takes a record; `actionClass` returns a string. One adapter, so the action
 *  chrome stays THE source of a button's classes even where a state class is added. */
function clsMap(classes: string): Record<string, boolean> {
  return Object.fromEntries(classes.split(" ").filter(Boolean).map((c) => [c, true]))
}
